import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Check, X, Clock, Play, Pause, StopCircle, RefreshCw, ChevronDown, ChevronRight,
  Workflow
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '../../services/api';
import { trackUsage } from '../../services/usageTracker';
import { extrahiereProgrammeId } from '../../lib/usageRoute';
import { useLocation } from 'react-router-dom';
import { useFlowExecution } from '../../hooks/useFlowExecution';
import { StatusBadge } from './FlowHelpers';

// =====================
// Types
// =====================

interface StepState {
  stepId: string;
  name: string;
  status: string;
  order: number;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  rowCountIn?: number;
  rowCountOut?: number;
  error?: string;
  retryCount?: number;
  progressMessage?: string;
  progress?: number;
}

// =====================
// Helpers
// =====================

const formatDate = (dateString?: string | null) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatDuration = (ms?: number | null) => {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
};

// =====================
// Step Status Icon
// =====================

const StepIcon: React.FC<{ status: string }> = ({ status }) => {
  const iconStyle: React.CSSProperties = {
    width: '28px', height: '28px', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  };

  switch (status) {
    case 'completed':
      return <div style={{ ...iconStyle, backgroundColor: '#D1FAE5' }}><Check size={14} color="#065F46" /></div>;
    case 'failed':
      return <div style={{ ...iconStyle, backgroundColor: '#FEE2E2' }}><X size={14} color="#991B1B" /></div>;
    case 'running':
      return <div style={{ ...iconStyle, backgroundColor: '#FEF3C7' }}><RefreshCw size={14} color="#92400E" className="spin" /></div>;
    case 'paused':
      return <div style={{ ...iconStyle, backgroundColor: '#DBEAFE' }}><Pause size={14} color="#1E40AF" /></div>;
    case 'pending':
    default:
      return <div style={{ ...iconStyle, backgroundColor: '#F3F4F6', border: '2px solid #D1D5DB' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#9CA3AF' }} /></div>;
  }
};

// =====================
// Step Data Viewer
// =====================

interface StepDataViewerProps {
  executionId: string;
  stepId: string;
}

const StepDataViewer: React.FC<StepDataViewerProps> = ({ executionId, stepId }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState<'input' | 'output'>('output');

  const load = async () => {
    if (loaded) return;
    setLoading(true);
    try {
      const res = await api.flows.getStepData(executionId, stepId);
      if (res.success) setData(res.data);
      setLoaded(true);
    } catch {
      toast.error('Fehler beim Laden der Step-Daten');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center' }}>
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1A1A1A] mx-auto"></div>
      </div>
    );
  }

  if (!data) return null;

  const displayData = tab === 'input' ? data.inputData : data.outputData;

  return (
    <div style={{ marginTop: '0.75rem' }}>
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.5rem' }}>
        {(['output', 'input'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-0.5 text-xs border rounded ${
              tab === t
                ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                : 'bg-transparent text-gray-500 border-[#DAD9D9]'
            }`}
            style={{ cursor: 'pointer' }}
          >
            {t === 'input' ? 'Input' : 'Output'}
          </button>
        ))}
      </div>
      <pre style={{
        backgroundColor: '#1e1e1e', color: '#d4d4d4',
        padding: '1rem', borderRadius: '0.5rem',
        fontSize: '0.75rem', overflowX: 'auto', maxHeight: '300px',
        overflowY: 'auto', margin: 0,
      }}>
        {JSON.stringify(displayData, null, 2)}
      </pre>
    </div>
  );
};

// =====================
// Step Item
// =====================

interface StepItemProps {
  step: StepState;
  executionId: string;
  isLast: boolean;
}

const StepItem: React.FC<StepItemProps> = ({ step, executionId, isLast }) => {
  const [expanded, setExpanded] = useState(step.status === 'failed');

  return (
    <div style={{ display: 'flex', gap: '1rem' }}>
      {/* Timeline connector */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <StepIcon status={step.status} />
        {!isLast && (
          <div className="w-0.5 flex-1 bg-[#DAD9D9]" style={{ minHeight: '24px', margin: '4px 0' }} />
        )}
      </div>

      {/* Step content */}
      <div style={{ flex: 1, paddingBottom: isLast ? 0 : '1rem' }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.25rem' }}
          onClick={() => setExpanded(e => !e)}
        >
          <span className="font-medium text-[0.9rem] text-[#1A1A1A]">{step.name}</span>
          <StatusBadge status={step.status} />
          {step.durationMs !== undefined && (
            <span className="text-xs text-gray-400">{formatDuration(step.durationMs)}</span>
          )}
          {(step.rowCountIn !== undefined || step.rowCountOut !== undefined) && (
            <span className="text-xs text-gray-400">
              {step.rowCountIn !== undefined && `In: ${step.rowCountIn}`}
              {step.rowCountIn !== undefined && step.rowCountOut !== undefined && ' / '}
              {step.rowCountOut !== undefined && `Out: ${step.rowCountOut}`}
            </span>
          )}
          <div style={{ marginLeft: 'auto' }}>
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </div>
        </div>

        {/* Progress message while running */}
        {step.status === 'running' && step.progressMessage && (
          <div className="text-xs text-gray-500 mb-1 italic">
            {step.progressMessage}
            {step.progress !== undefined && (
              <span className="ml-2 text-gray-400">({step.progress}%)</span>
            )}
          </div>
        )}

        {expanded && (
          <div className="bg-gray-50 rounded-lg p-3">
            {step.error && (
              <div style={{
                backgroundColor: '#FEE2E2', color: '#991B1B',
                padding: '0.75rem', borderRadius: '0.375rem',
                fontSize: '0.8rem', marginBottom: '0.75rem',
                fontFamily: 'monospace',
              }}>
                {step.error}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <div className="text-xs">
                <span className="text-gray-400">Start: </span>
                {formatDate(step.startedAt)}
              </div>
              <div className="text-xs">
                <span className="text-gray-400">Ende: </span>
                {formatDate(step.completedAt)}
              </div>
            </div>
            {step.retryCount !== undefined && step.retryCount > 0 && (
              <div style={{ fontSize: '0.75rem', color: '#92400E', marginBottom: '0.5rem' }}>
                Retries: {step.retryCount}
              </div>
            )}
            {(step.status === 'completed' || step.status === 'failed') && (
              <StepDataViewer executionId={executionId} stepId={step.stepId} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// =====================
// Main Component
// =====================

interface FlowCatalogInfo {
  description?: string;
  steps?: Array<{ id: string; name: string; description: string; order: number }>;
}

interface FlowExecutionDetailProps {
  executionId: string;
  onBack: () => void;
}

const FlowExecutionDetail: React.FC<FlowExecutionDetailProps> = ({ executionId, onBack }) => {
  const { execution, loading, error, refetch } = useFlowExecution(executionId);
  const [actionLoading, setActionLoading] = useState(false);
  const [flowInfo, setFlowInfo] = useState<FlowCatalogInfo | null>(null);
  const { pathname } = useLocation();

  // Nutzungsspur: Öffnen einer Flow-Execution (einmal je executionId)
  useEffect(() => {
    if (!executionId) return;
    trackUsage({
      eventType: 'result_view',
      refType: 'flow_execution',
      refId: String(executionId),
      programmeId: extrahiereProgrammeId(pathname),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId]);

  useEffect(() => {
    if (!execution?.flowId) return;
    api.flows.getCatalogItem(execution.flowId)
      .then((res: any) => { if (res.success) setFlowInfo(res.data); })
      .catch(() => {});
  }, [execution?.flowId]);

  const handleCancel = async () => {
    if (!executionId) return;
    setActionLoading(true);
    try {
      const res = await api.flows.cancelExecution(executionId);
      if (res.success) {
        toast.success('Ausführung abgebrochen');
        refetch();
      }
    } catch {
      toast.error('Fehler beim Abbrechen');
    } finally {
      setActionLoading(false);
    }
  };

  const handleContinue = async () => {
    if (!executionId) return;
    setActionLoading(true);
    try {
      const res = await api.flows.continueExecution(executionId);
      if (res.success) {
        toast.success('Ausführung fortgesetzt');
        refetch();
      }
    } catch {
      toast.error('Fehler beim Fortsetzen');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A1A1A] mx-auto"></div>
        <span className="text-sm text-gray-500 mt-2 block">Lade Ausführung...</span>
      </div>
    );
  }

  if (error || !execution) {
    return (
      <div className="text-center p-8">
        <X size={40} color="#EF4444" />
        <p className="mt-4 text-gray-500">{error || 'Ausführung nicht gefunden'}</p>
        <button
          className="mt-4 inline-flex items-center gap-1 px-3 py-1.5 border border-[#DAD9D9] rounded text-sm text-[#1A1A1A] hover:bg-gray-50"
          onClick={onBack}
        >
          <ArrowLeft size={16} />
          Zurück
        </button>
      </div>
    );
  }

  const steps: StepState[] = execution.stepStates || [];
  const canCancel = execution.status === 'running' || execution.status === 'paused';
  const canContinue = execution.status === 'paused';

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <button
          className="inline-flex items-center px-3 py-1.5 border border-[#DAD9D9] rounded text-sm text-[#1A1A1A] hover:bg-gray-50"
          onClick={onBack}
        >
          <ArrowLeft size={16} />
        </button>
        <div>
          <h1 className="text-xl font-bold text-[#1A1A1A] flex items-center gap-2">
            <Workflow size={24} />
            {execution.flowName || execution.flowId}
          </h1>
          <p className="text-xs text-gray-400">Execution ID: {execution.id}</p>
        </div>
      </div>

      {/* Flow Description */}
      {flowInfo?.description && (
        <p className="text-sm text-gray-500 mb-4 -mt-2">
          {flowInfo.description}
        </p>
      )}

      {/* Meta-Info Header */}
      <div className="border border-[#DAD9D9] rounded-lg mb-6">
        <div className="p-5 flex gap-8 flex-wrap items-center">
          <div>
            <StatusBadge status={execution.status} large />
          </div>
          <div>
            <div className="text-xs text-gray-400 mb-0.5">Gestartet von</div>
            <div className="font-medium text-[#1A1A1A]">{execution.startedByUser?.username || '-'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-400 mb-0.5">Gestartet am</div>
            <div className="font-medium text-[#1A1A1A]">{formatDate(execution.startedAt)}</div>
          </div>
          {execution.completedAt && (
            <div>
              <div className="text-xs text-gray-400 mb-0.5">Beendet am</div>
              <div className="font-medium text-[#1A1A1A]">{formatDate(execution.completedAt)}</div>
            </div>
          )}
          <div>
            <div className="text-xs text-gray-400 mb-0.5">Dauer</div>
            <div className="font-medium text-[#1A1A1A]">{formatDuration(execution.durationMs)}</div>
          </div>

          {/* Action buttons */}
          <div className="ml-auto flex gap-2">
            <button
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#DAD9D9] rounded text-sm text-[#1A1A1A] hover:bg-gray-50"
              onClick={refetch}
              disabled={actionLoading}
            >
              <RefreshCw size={16} />
              Aktualisieren
            </button>
            {canContinue && (
              <button
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#E9FF57] text-[#1A1A1A] rounded text-sm font-medium hover:bg-[#d4ea4d]"
                onClick={handleContinue}
                disabled={actionLoading}
              >
                <Play size={16} />
                Fortsetzen
              </button>
            )}
            {canCancel && (
              <button
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white rounded text-sm font-medium hover:bg-red-600"
                onClick={handleCancel}
                disabled={actionLoading}
              >
                <StopCircle size={16} />
                Abbrechen
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error message at top level */}
      {execution.errorMessage && (
        <div style={{
          backgroundColor: '#FEE2E2', color: '#991B1B', padding: '1rem',
          borderRadius: '0.5rem', marginBottom: '1.5rem', fontFamily: 'monospace', fontSize: '0.875rem',
        }}>
          {execution.errorMessage}
        </div>
      )}

      {/* Steps Timeline */}
      <div>
        <h3 className="mb-5 text-base font-semibold text-[#1A1A1A]">Steps ({steps.length})</h3>

        {steps.length === 0 ? (
          <div className="border border-[#DAD9D9] rounded-lg text-center p-8">
            <Clock size={32} color="#9CA3AF" />
            <p className="mt-3 text-gray-500">Noch keine Steps ausgeführt</p>
          </div>
        ) : (
          <div className="border border-[#DAD9D9] rounded-lg">
            <div className="p-5">
              {steps.map((step, index) => (
                <StepItem
                  key={step.stepId}
                  step={step}
                  executionId={execution.id}
                  isLast={index === steps.length - 1}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlowExecutionDetail;
